import { Toaster as Sonner } from 'sonner';

import { useIsPhone } from '@/hooks/useIsPhone';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * The app's one toast surface — DESIGN.md {component.toast}: {colors.panel} on a {colors.line}
 * hairline, {rounded.md}, 11px 16px, icon-led (✓ in the ok text tier, ✕ in the bad text tier), no
 * side stripes. It was sonner's LIGHT default: every write confirmation flashed a white slab on
 * the dark console.
 *
 * Where (DESIGN.md §Responsive "Toasts"):
 *   - phone: top-centre, under the shell's whole top band — header AND section chips
 *     (`--sh-band-bottom`, measured live by ShellV2; the header's bottom, then the status bar's
 *     safe area, before it exists) — the full width less the 14px gutters. Never over the bell and
 *     avatar it used to cover, nor over the section chips (an error toast is sticky, and it hid
 *     the section nav until dismissed);
 *   - desktop: top-right, as before (staging, 2026-10-13: staging has no dock, and its mini
 *     player runs along the bottom, so the redesign's bottom-right lane would sit on it). The
 *     redesign keeps bottom-right, above its dock.
 *
 * Sonner draws a toast from its own CSS variables, which its theme block sets with selectors no
 * utility class outranks — so the surface is set by overriding those variables on the toaster.
 *
 * Lifetime: success and neutral toasts auto-dismiss at 4s (sonner's default). Errors stay until
 * dismissed: `toast.error` from `@/lib/toast` gives them `duration: Infinity` and a ×. The × is
 * restyled here: sonner draws a 20px bordered disc hanging off the top-left corner; this draws a
 * 20px glyph at the right edge, inside the toast, with a 44px hit area. An error toast reserves
 * room for it on the right.
 */
const SURFACE = {
  '--normal-bg': 'var(--sh-panel)',
  '--normal-border': 'var(--sh-line)',
  '--normal-text': 'var(--sh-text)',
  '--border-radius': 'var(--sh-r-md)',
} as React.CSSProperties;

// Under the whole top band as it is while the header shows (ShellV2 publishes it only when the band
// really changes size). The toasts do not follow the header's slide: a toast shown while it is
// hidden simply sits a little lower than the chips.
const PHONE_TOP = 'calc(var(--sh-band-bottom, calc(48px + env(safe-area-inset-top))) + 8px)';

const Toaster = ({ style, toastOptions, ...props }: ToasterProps) => {
  const isPhone = useIsPhone();

  return (
    <Sonner
      theme="dark"
      className="toaster group"
      position={isPhone ? 'top-center' : 'top-right'}
      offset={
        isPhone
          ? { top: PHONE_TOP, left: '14px', right: '14px' }
          : { top: '32px', right: '24px' }
      }
      // Sonner's own "mobile" layout starts below 600px; the phone layout here runs to 879px.
      mobileOffset={{ top: PHONE_TOP, left: '14px', right: '14px' }}
      visibleToasts={3}
      style={{ ...SURFACE, pointerEvents: 'auto', ...style }}
      toastOptions={{
        ...toastOptions,
        classNames: {
          // The hairline is drawn here too: sonner's `border: 1px solid var(--normal-border)` sits in
          // a zero-specificity `:where()`, and Tailwind's preflight (`border-width: 0`, loaded
          // later) wins over it — every toast was a borderless slab.
          toast: '!gap-2.5 !px-4 !py-[11px] !shadow-dock !border !border-solid !border-[var(--normal-border)]',
          title: '!text-[14px] !font-semibold !leading-snug',
          description: '!text-[13px] !text-[var(--sh-mid)] line-clamp-3',
          success: '[&_[data-icon]]:!text-[var(--sh-ok-text)]',
          error: '[&_[data-icon]]:!text-[var(--sh-bad-text)] !pr-12',
          closeButton:
            '!left-auto !right-0.5 !top-1/2 ![transform:translateY(-50%)] !h-11 !w-11 !rounded-[var(--sh-r-md)] !border-0 !bg-transparent !text-[var(--sh-mid)] hover:!text-[var(--sh-text)] [&>svg]:!h-5 [&>svg]:!w-5',
          actionButton: '!bg-[var(--sh-violet)] !text-[var(--sh-on-violet)]',
          cancelButton: '!bg-[var(--sh-tile-2)] !text-[var(--sh-mid)]',
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
